import { Link } from "react-router-dom";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Download } from "lucide-react";
import { Helmet } from "react-helmet-async";
import electrificationWhitepaperFull from "@/assets/electrification-whitepaper-full.png.asset.json";

const WhitePaperElectrificationIntro = () => {
  return (
    <>
      <Helmet>
        <title>Is your fleet ready for electrification? White Paper | Cetasol</title>
        <meta
          name="description"
          content="Electrification white paper: how operational data, digital twins and AI help you choose the right electrification path for each vessel and across the fleet."
        />
        <link rel="canonical" href="https://cetasol.com/electrification-white-paper" />
        <meta property="og:title" content="Is your fleet ready for electrification? White Paper | Cetasol" />
        <meta property="og:description" content="Electrification white paper: how operational data, digital twins and AI help you choose the right electrification path for each vessel and across the fleet." />
        <meta property="og:type" content="article" />
        <meta property="og:url" content="https://cetasol.com/electrification-white-paper" />
        <meta property="og:image" content="https://cetasol.com/og-image.jpg" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Is your fleet ready for electrification? White Paper | Cetasol" />
        <meta name="twitter:description" content="Electrification white paper: how operational data, digital twins and AI help you choose the right electrification path for each vessel and across the fleet." />
        <meta name="twitter:image" content="https://cetasol.com/og-image.jpg" />
      </Helmet>
      <Header />
      <main className="min-h-screen pt-24 pb-16">
        <article className="container mx-auto px-6 max-w-3xl">
          <Link
            to="/whitepapers"
            className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors mb-8"
          >
            <ArrowLeft size={16} /> Back to all white papers
          </Link>

          <header className="mb-8">
            <h1 className="text-3xl md:text-4xl font-bold mb-4">
              The future of maritime operations: Is your fleet ready for electrification?
            </h1>
            <Link to="/whitepaper/electrification-download" className="inline-block mt-6">
              <Button className="gap-2">
                <Download size={16} /> Download PDF
              </Button>
            </Link>
          </header>

          <img
            src={electrificationWhitepaperFull.url}
            alt="Electrification White Paper cover"
            className="w-full rounded-lg mb-8"
          />

          <div className="prose prose-invert prose-lg max-w-none prose-p:text-foreground/80 prose-p:leading-relaxed prose-p:mb-4">
            <p>
              Electrification has become a key topic across the maritime industry, but successful electrification is about far more than replacing fuel with batteries. Every vessel has its own energy demand, operating pattern and infrastructure constraints, and a solution that works well for one vessel may be unsuitable for another.
            </p>
            <p>
              This white paper walks through the challenges shipowners, operators and technical teams face, from upfront investment and uncertain ROI to range, charging and system integration. It shows why electrification is not all or nothing, and how full, hybrid or partial electrification can each be the right answer depending on the operation.
            </p>
            <p>
              It also covers the 9 critical questions to answer before electrifying, how real operational data builds an accurate energy profile for battery sizing and charging, and how digital twins, simulation and AI make it possible to test scenarios before you invest.
            </p>
            <p>
              The future of maritime electrification is not about electrifying everything. It is about electrifying smarter. Download the full white paper to see how insight turns electrification into a decision you can stand behind.
            </p>
          </div>

          <div className="mt-10 flex justify-center">
            <Link to="/whitepaper/electrification-download">
              <Button className="gap-2">
                <Download size={16} /> Download PDF
              </Button>
            </Link>
          </div>
        </article>
      </main>
      <Footer />
    </>
  );
};

export default WhitePaperElectrificationIntro;
